import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Plus, Play, Pause, Trash2, Calendar, Tag, Layers,
  ChevronRight, MessageSquare, Sparkles,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { workflowsApi, Workflow } from '../../../../services/workflowsApi';
import WorkflowTemplateGallery from './WorkflowTemplateGallery';
import WorkflowEditor from './WorkflowEditor';

interface WorkflowsPanelProps {
  clientId: string;
}

type View =
  | { kind: 'list' }
  | { kind: 'gallery' }
  | { kind: 'editor'; workflowId: string };

export const WorkflowsPanel: React.FC<WorkflowsPanelProps> = ({ clientId }) => {
  const [view, setView] = useState<View>({ kind: 'list' });
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await workflowsApi.list(clientId);
      setWorkflows(items);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (view.kind === 'list') load();
  }, [view, load]);

  if (view.kind === 'gallery') {
    return (
      <WorkflowTemplateGallery
        clientId={clientId}
        onCancel={() => setView({ kind: 'list' })}
        onCreated={(wf) => setView({ kind: 'editor', workflowId: wf.id })}
      />
    );
  }

  if (view.kind === 'editor') {
    return (
      <WorkflowEditor
        clientId={clientId}
        workflowId={view.workflowId}
        onClose={() => setView({ kind: 'list' })}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Workflows</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Schedule daily check-ins, drips, surveys — anything sequential.
          </p>
        </div>
        <button
          onClick={() => setView({ kind: 'gallery' })}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
        >
          <Plus size={15} />
          New workflow
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
          <Loader2 size={18} className="animate-spin inline-block mr-2" />
          Loading workflows…
        </div>
      ) : workflows.length === 0 ? (
        <EmptyState onCreate={() => setView({ kind: 'gallery' })} />
      ) : (
        <div className="space-y-2">
          {workflows.map((wf) => (
            <WorkflowRow
              key={wf.id}
              clientId={clientId}
              workflow={wf}
              onOpen={() => setView({ kind: 'editor', workflowId: wf.id })}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100 p-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
      <Sparkles size={22} className="text-emerald-600" />
    </div>
    <h4 className="font-medium text-gray-900 mb-1">No workflows yet</h4>
    <p className="text-sm text-gray-500 max-w-md mx-auto mb-5 leading-relaxed">
      Pick a starter template — daily check-ins, course drip, surveys — and customize the questions
      and schedule. We handle the sending.
    </p>
    <button
      onClick={onCreate}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
    >
      <Plus size={15} />
      Browse templates
    </button>
  </div>
);

const WorkflowRow: React.FC<{
  clientId: string;
  workflow: Workflow;
  onOpen: () => void;
  onChanged: () => void;
}> = ({ clientId, workflow: wf, onOpen, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const togglePause = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await workflowsApi.update(clientId, wf.id, { active: !wf.active });
      toast.success(wf.active ? 'Workflow paused' : 'Workflow activated');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${wf.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await workflowsApi.remove(clientId, wf.id);
      toast.success('Deleted');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const stepCount = wf.definition?.steps?.length ?? 0;
  const triggerLabel = describeTrigger(wf);
  const audienceLabel = describeAudience(wf);

  return (
    <div
      onClick={onOpen}
      className="group bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer p-4 flex items-center gap-4"
    >
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          wf.active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'
        }`}
      >
        <Layers size={18} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 truncate">{wf.name}</span>
          <ActiveBadge active={wf.active} />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <MessageSquare size={11} /> {stepCount} {stepCount === 1 ? 'step' : 'steps'}
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} /> {triggerLabel}
          </span>
          {audienceLabel && (
            <span className="inline-flex items-center gap-1">
              <Tag size={11} /> {audienceLabel}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={togglePause}
          disabled={busy}
          title={wf.active ? 'Pause' : 'Activate'}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : wf.active ? <Pause size={15} /> : <Play size={15} />}
        </button>
        <button
          onClick={remove}
          disabled={busy}
          title="Delete"
          className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 size={15} />
        </button>
        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500" />
      </div>
    </div>
  );
};

const ActiveBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
      active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
    {active ? 'Active' : 'Paused'}
  </span>
);

function describeTrigger(wf: Workflow): string {
  const t = wf.trigger || {};
  if (t.type === 'schedule') {
    if (t.per_recipient_field === 'preferred_time_local') return 'Daily at recipient time';
    if (t.cron) return `Cron: ${t.cron}`;
    return 'Scheduled';
  }
  if (t.type === 'manual') return 'Manual';
  if (t.type === 'event') return `Event: ${t.event || '—'}`;
  return '—';
}

function describeAudience(wf: Workflow): string | null {
  const a = wf.trigger?.audience;
  if (!a) return null;
  if (a.all) return 'Everyone opted-in';
  if (a.tags && a.tags.length) return `Tags: ${a.tags.join(', ')}`;
  if (a.all_tags && a.all_tags.length) return `All tags: ${a.all_tags.join(', ')}`;
  if (a.ids && a.ids.length) return `${a.ids.length} specific`;
  return null;
}

export default WorkflowsPanel;
